import React, { useMemo, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material"

import { ImageWidget } from "../CustomWidgets"
import { RaffleSelectWidget } from "../RaffleSelectWidget"
import { WheelServicePickerWidget } from "./WheelServicePickerWidget"
import { WHEEL_PRIZE_TYPE_LABELS, WHEEL_PRIZE_TYPES, WheelPrizeFormData, WheelPrizeType, createBlankPrize } from "./wheelPrizeTypes"
import { PrizeFieldErrors, validatePrize } from "./wheelPrizeValidation"

const millisToLocalInput = (millis: number | null): string => {
  if (!millis) return ""
  const date = new Date(millis)
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const localInputToMillis = (value: string): number | null => {
  if (!value) return null
  const millis = new Date(value).getTime()
  return Number.isFinite(millis) ? millis : null
}

const numberOrNull = (value: string): number | null => {
  if (value.trim() === "") return null
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

export const WheelPrizeEditor: React.FC<{
  open: boolean
  initialPrize: WheelPrizeFormData | null
  nextSortOrder: number
  onClose: () => void
  onSave: (prize: WheelPrizeFormData) => void
}> = ({ open, initialPrize, nextSortOrder, onClose, onSave }) => {
  const [prize, setPrize] = useState<WheelPrizeFormData>(initialPrize ?? createBlankPrize("facilCoins", nextSortOrder))
  const [touched, setTouched] = useState(false)

  useMemo(() => {
    setPrize(initialPrize ?? createBlankPrize("facilCoins", nextSortOrder))
    setTouched(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrize, open])

  const errors: PrizeFieldErrors = touched ? validatePrize(prize) : {}

  const patch = (next: Partial<WheelPrizeFormData>) => setPrize((prev) => ({ ...prev, ...next }))

  const handleTypeChange = (type: WheelPrizeType) => {
    // Switching type keeps common fields (label, image, weight, caps) but replaces the
    // type-specific ones with fresh defaults, since e.g. a `raffleId` from a raffleEntry
    // prize is meaningless once switched to `cash`.
    const blank = createBlankPrize(type, prize.sortOrder)
    setPrize({
      ...blank,
      id: prize.id,
      label: prize.label,
      imageUrl: prize.imageUrl,
      weight: prize.weight,
      enabled: prize.enabled,
      winning: prize.winning,
    })
  }

  const handleSave = () => {
    setTouched(true)
    if (Object.keys(validatePrize(prize)).length > 0) {
      return
    }
    onSave(prize)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initialPrize ? "Edit prize" : "Add prize"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <TextField
            select
            label="Prize type"
            value={prize.type}
            onChange={(event) => handleTypeChange(event.target.value as WheelPrizeType)}
            fullWidth
          >
            {WHEEL_PRIZE_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {WHEEL_PRIZE_TYPE_LABELS[type]}
              </MenuItem>
            ))}
          </TextField>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Label"
                value={prize.label}
                onChange={(event) => patch({ label: event.target.value })}
                error={!!errors.label}
                helperText={errors.label}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Short label (on the wheel segment)"
                value={prize.shortLabel ?? ""}
                onChange={(event) => patch({ shortLabel: event.target.value || null })}
                helperText="Optional — abbreviated text shown on the wheel itself"
                fullWidth
              />
            </Grid>
          </Grid>

          <TextField
            label="Description"
            value={prize.description ?? ""}
            onChange={(event) => patch({ description: event.target.value || null })}
            multiline
            minRows={2}
            fullWidth
          />

          <Box sx={{ width: 220 }}>
            <ImageWidget label="Image" value={prize.imageUrl ?? ""} onChange={(value: string) => patch({ imageUrl: value || null })} />
          </Box>

          <Divider />
          <Typography variant="subtitle2">Prize details</Typography>

          {prize.type === "facilCoins" && (
            <TextField
              label="Facil-Coins amount"
              type="number"
              value={prize.amount ?? 0}
              onChange={(event) => patch({ amount: Number(event.target.value) })}
              error={!!errors.amount}
              helperText={errors.amount}
              inputProps={{ min: 0 }}
            />
          )}

          {prize.type === "product" && (
            <Stack spacing={1}>
              <WheelServicePickerWidget
                sponsorId={prize.sponsorId}
                serviceId={prize.serviceId}
                onChange={({ sponsorId, serviceId, serviceTitle, imageUrl }) =>
                  patch({
                    sponsorId,
                    serviceId,
                    serviceTitle,
                    imageUrl: prize.imageUrl ?? imageUrl,
                    label: prize.label || serviceTitle,
                  })
                }
              />
              {errors.serviceId && <Alert severity="error">{errors.serviceId}</Alert>}
              <Alert severity="info">
                Winning this prize never auto-marks it as delivered — it creates a pending fulfillment record for you to
                complete manually.
              </Alert>
            </Stack>
          )}

          {prize.type === "raffleEntry" && (
            <Stack spacing={2}>
              <Box>
                {/* RaffleSelectWidget is an RJSF widget; outside RJSF it only needs value/onChange. */}
                <RaffleSelectWidget
                  {...({
                    value: prize.raffleId ?? "",
                    onChange: (raffleId: string) => patch({ raffleId }),
                  } as any)}
                />
                {errors.raffleId && (
                  <Typography color="error" variant="caption">
                    {errors.raffleId}
                  </Typography>
                )}
              </Box>
              <TextField
                label="Tickets per win"
                type="number"
                value={prize.ticketsCount ?? 1}
                onChange={(event) => patch({ ticketsCount: Number(event.target.value) })}
                error={!!errors.ticketsCount}
                helperText={errors.ticketsCount}
                inputProps={{ min: 1 }}
              />
            </Stack>
          )}

          {prize.type === "cash" && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Amount"
                  type="number"
                  value={prize.amount ?? 0}
                  onChange={(event) => patch({ amount: Number(event.target.value) })}
                  error={!!errors.amount}
                  helperText={errors.amount}
                  inputProps={{ min: 0, step: "0.01" }}
                  sx={{ flexGrow: 1 }}
                />
                <TextField
                  label="Currency"
                  value={prize.currency ?? "USD"}
                  onChange={(event) => patch({ currency: event.target.value.toUpperCase() })}
                  error={!!errors.currency}
                  helperText={errors.currency}
                  sx={{ width: 140 }}
                />
              </Stack>
              <TextField
                label="Fulfillment instructions (shown to the winner and to you)"
                value={prize.fulfillmentInstructions ?? ""}
                onChange={(event) => patch({ fulfillmentInstructions: event.target.value || null })}
                multiline
                minRows={2}
              />
              <Alert severity="info">
                Cash prizes are tracked as a pending payout record — WinFacil doesn't move real money automatically.
              </Alert>
            </Stack>
          )}

          {prize.type === "coupon" && (
            <Stack spacing={2}>
              <Alert severity="info">
                Coupons reuse WinFacil's existing sponsor-offer/service coupon system. Pick a service for a live picker, or
                type a sponsor + offer id if the coupon belongs to a specific monthly offer.
              </Alert>
              <WheelServicePickerWidget
                sponsorId={prize.sponsorId}
                serviceId={prize.serviceId}
                onChange={({ sponsorId, serviceId, serviceTitle }) =>
                  patch({ sponsorId, serviceId, offerId: null, offerTitle: serviceTitle })
                }
              />
              <Divider>or</Divider>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Sponsor ID"
                  value={prize.sponsorId ?? ""}
                  onChange={(event) => patch({ sponsorId: event.target.value, serviceId: undefined })}
                  sx={{ flexGrow: 1 }}
                />
                <TextField
                  label="Offer ID"
                  value={prize.offerId ?? ""}
                  onChange={(event) => patch({ offerId: event.target.value || null })}
                  sx={{ flexGrow: 1 }}
                />
              </Stack>
              <TextField
                label="Offer / coupon title shown to the winner"
                value={prize.offerTitle ?? ""}
                onChange={(event) => patch({ offerTitle: event.target.value || null })}
              />
              {errors.sponsorId && (
                <Typography color="error" variant="caption">
                  {errors.sponsorId}
                </Typography>
              )}
              {errors.offerId && (
                <Typography color="error" variant="caption">
                  {errors.offerId}
                </Typography>
              )}
            </Stack>
          )}

          {prize.type === "bonusSpin" && (
            <TextField
              label="Extra spins awarded"
              type="number"
              value={prize.spinsAwarded ?? 1}
              onChange={(event) => patch({ spinsAwarded: Number(event.target.value) })}
              error={!!errors.spinsAwarded}
              helperText={errors.spinsAwarded ?? "Capped at 3 per prize to prevent abuse."}
              inputProps={{ min: 1, max: 3 }}
            />
          )}

          {prize.type === "custom" && (
            <Stack spacing={2}>
              <TextField
                label="Call-to-action label"
                value={prize.ctaLabel ?? ""}
                onChange={(event) => patch({ ctaLabel: event.target.value || null })}
              />
              <TextField
                label="Reference URL (optional, http/https only)"
                value={prize.referenceUrl ?? ""}
                onChange={(event) => patch({ referenceUrl: event.target.value || null })}
                error={!!errors.referenceUrl}
                helperText={errors.referenceUrl}
              />
              <TextField
                label="Fulfillment instructions"
                value={prize.fulfillmentInstructions ?? ""}
                onChange={(event) => patch({ fulfillmentInstructions: event.target.value || null })}
                multiline
                minRows={2}
              />
              <TextField
                label="Internal admin notes (not shown to users)"
                value={prize.adminNotes ?? ""}
                onChange={(event) => patch({ adminNotes: event.target.value || null })}
                multiline
                minRows={2}
              />
            </Stack>
          )}

          <Divider />
          <Typography variant="subtitle2">Odds, availability & inventory</Typography>

          <Stack direction="row" spacing={2} flexWrap="wrap">
            <TextField
              label="Weight (relative odds)"
              type="number"
              value={prize.weight}
              onChange={(event) => patch({ weight: Number(event.target.value) })}
              error={!!errors.weight}
              helperText={errors.weight}
              inputProps={{ min: 1 }}
              sx={{ width: 200 }}
            />
            <FormControlLabel
              control={<Checkbox checked={prize.enabled} onChange={(event) => patch({ enabled: event.target.checked })} />}
              label="Enabled"
            />
            <FormControlLabel
              control={<Checkbox checked={prize.featured} onChange={(event) => patch({ featured: event.target.checked })} />}
              label="Featured"
            />
            <FormControlLabel
              control={<Checkbox checked={prize.grandPrize} onChange={(event) => patch({ grandPrize: event.target.checked })} />}
              label="Grand prize"
            />
            <FormControlLabel
              control={<Checkbox checked={prize.winning} onChange={(event) => patch({ winning: event.target.checked })} />}
              label="Winning item"
            />
          </Stack>
          {!prize.winning && (
            <Alert severity="info">
              Landing on this prize will show "Try Again" instead of "YOU WON" to the user, and will never trigger a
              winner announcement.
            </Alert>
          )}

          <Stack direction="row" spacing={2} flexWrap="wrap">
            <TextField
              label="Total quantity (lifetime cap)"
              type="number"
              value={prize.totalQuantity ?? ""}
              onChange={(event) => patch({ totalQuantity: numberOrNull(event.target.value) })}
              error={!!errors.totalQuantity}
              helperText={errors.totalQuantity ?? "Blank = unlimited"}
              inputProps={{ min: 1 }}
              sx={{ width: 220 }}
            />
            <TextField
              label="Quantity per day"
              type="number"
              value={prize.quantityPerDay ?? ""}
              onChange={(event) => patch({ quantityPerDay: numberOrNull(event.target.value) })}
              error={!!errors.quantityPerDay}
              helperText={errors.quantityPerDay ?? "Blank = unlimited"}
              inputProps={{ min: 1 }}
              sx={{ width: 220 }}
            />
            <TextField
              label="Per-user limit"
              type="number"
              value={prize.perUserLimit ?? ""}
              onChange={(event) => patch({ perUserLimit: numberOrNull(event.target.value) })}
              error={!!errors.perUserLimit}
              helperText={errors.perUserLimit ?? "Blank = unlimited"}
              inputProps={{ min: 1 }}
              sx={{ width: 220 }}
            />
          </Stack>

          <Stack direction="row" spacing={2} flexWrap="wrap">
            <TextField
              label="Available from"
              type="datetime-local"
              value={millisToLocalInput(prize.startAtMillis)}
              onChange={(event) => patch({ startAtMillis: localInputToMillis(event.target.value) })}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 240 }}
            />
            <TextField
              label="Available until"
              type="datetime-local"
              value={millisToLocalInput(prize.endAtMillis)}
              onChange={(event) => patch({ endAtMillis: localInputToMillis(event.target.value) })}
              error={!!errors.endAtMillis}
              helperText={errors.endAtMillis}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 240 }}
            />
          </Stack>

          <Divider />
          <Typography variant="subtitle2">Winner announcement</Typography>
          <TextField
            select
            label="Announce this prize's winners"
            value={prize.announceWinner === null ? "inherit" : prize.announceWinner ? "always" : "never"}
            onChange={(event) => {
              const value = event.target.value
              patch({ announceWinner: value === "inherit" ? null : value === "always" })
            }}
            sx={{ width: 260 }}
          >
            <MenuItem value="inherit">Use global policy</MenuItem>
            <MenuItem value="always">Always announce</MenuItem>
            <MenuItem value="never">Never announce</MenuItem>
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          Save prize
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default WheelPrizeEditor
